import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { ChartDataset, TooltipCallbacks } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { isSameDay } from 'date-fns';
import { Anlegg, Verdi } from 'src/app/services/forbruk/forbruk.types';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from '../../../../forbruk/charts/forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { StromstotteService } from 'src/app/services/stromstotte/stromstotte.service';
import { Subscription } from 'rxjs';
import { theTime } from 'src/app/utils/date-utils';

@Component({
  selector: 'app-hour-chart-price',
  templateUrl: './hour-chart-price.component.html',
  styleUrls: ['./hour-chart-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HourChartPriceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedDate: Date;
  @Input() valgtAnlegg: Anlegg;
  @Input() isStromstotteToggled: boolean;

  dataSets: ChartDataset<'line'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'line'>>;
  stotte: number[] = [];
  showLoader: boolean = true;
  subscription: Subscription;

  constructor(
    private _stromstotteService: StromstotteService,
    private _changeDetectionRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.valgtAnlegg && this.selectedDate) {
      this.getCalculations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.valgtAnlegg &&
      this.selectedDate &&
      hasChanged([changes?.valgtAnlegg, changes?.isStromstotteToggled])
    ) {
      this.getCalculations();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  getCalculations() {
    if (
      this.isStromstotteToggled &&
      new Date(2023, 8, 1) <= this.selectedDate
    ) {
      this.stotte = [];
      this.subscription = this._stromstotteService
        .stromstotteResultGrouped$(this.valgtAnlegg.objectid, this.selectedDate)
        .subscribe({
          next: (res) => {
            if (res !== undefined) {
              const stotteSelectedDay =
                res?.receivesHourlyGrouped[theTime(this.selectedDate).to(['YYYY', 'MM', 'DD'])] || [];
              this.stotte = [];
              for (let i = 0; i < 25; i++) {
                const stotteHour =
                  stotteSelectedDay.find(
                    (h) =>
                      h.period.toString().substring(8, 10) ===
                      (i + 1).toString().padStart(2, '0')
                  )?.receives || 0;
                this.stotte.push(stotteHour);
              }
            } else {
              this.stotte = [];
            }
            this.getChartData();
            this.showLoader = false;
            this._changeDetectionRef.detectChanges();
          },
          error: () => {
            // TODO: Handle?
            this.getChartData();
            this.showLoader = false;
          }
        });
    } else {
      this.getChartData();
      this.showLoader = false;
      this._changeDetectionRef.detectChanges();
    }
  }

  getChartData() {
    this.dataSets = [];
    this.labels = [];

    // Plusskunde produksjon
    let plusskundedataFound = false;
    const belopPluss = [];

    this.title = 'Kostnad';
    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'kr'),
      title: (tt) =>
        titleTooltipCallback(tt, 'hour', 'kr', 'forbruk_tooltip_chart2')
    };

    const data = this.valgtAnlegg.verdiList.map((value: Verdi) => {
      // Safari 12 er sær på hvilket format Date konstruktøren tar imot
      // Så vi må trikse litt med tidspunktene for å få riktig tidssone
      const [dateS, timeS] = value.tidspunkt.split('T');
      const valueHour = Number(timeS.slice(0, 2));
      const valueDate = new Date(dateS);
      valueDate.setHours(valueHour);

      if (isSameDay(valueDate, this.selectedDate)) {
        if (this.valgtAnlegg.plusskunde) {
          const plussEarned = value.belopPlussverdi
            ? Math.abs(value.belopPlussverdi)
            : 0;

          if (plussEarned) {
            plusskundedataFound = true;
            belopPluss.push(plussEarned);
          } else belopPluss.push(0);
        }

        return {
          label: valueDate,
          kraft: value.belopKraft || 0,
          nettleie: value.belopNettleie || 0
        };
      }
    });

    if (this.stotte.reduce((a, b) => a + b, 0) > 0) {
      let stotte: number[] = [];
      for (let i = 0; data.length > i; i++) {
        stotte.push(data[i]?.kraft - this.stotte[i]);
      }
      if (stotte.some((x) => !Number.isNaN(x))) {
        this.dataSets.push({
          label: 'Strøm inkl. støtte',
          pointStyle: 'line',
          fill: false,
          backgroundColor: '#002987',
          borderColor: '#002987',
          borderWidth: 2,
          pointRadius: 1,
          borderDash: [5, 5],
          data: stotte
        });
      }
    }

    this.labels = data.map((x) => x?.label);
    this.dataSets.push({
      label: 'Strøm',
      pointStyle: 'line',
      fill: false,
      backgroundColor: '#002987',
      borderColor: '#002987',
      borderWidth: 2,
      pointRadius: 1,
      data: data.map((x) => x?.kraft || 0)
    });
    if (this.valgtAnlegg.gjennomfakturering) {
      this.dataSets.push({
        label: 'Nettleie',
        pointStyle: 'line',
        fill: false,
        backgroundColor: '#ff80ff',
        borderColor: '#ff80ff',
        borderWidth: 2,
        pointRadius: 1,
        data: data.map((x) => x?.nettleie || 0)
      });
    }
    if (plusskundedataFound) {
      this.dataSets.push({
        label: 'Inntekt Plusskunde',
        pointStyle: 'line',
        fill: false,
        backgroundColor: '#26b41b',
        borderColor: '#26b41b',
        borderWidth: 2,
        pointRadius: 1,
        data: belopPluss
      });
    }
  }
}
